import { apiPath } from "../../lib/apiPath";
import type {
  BattlePassLevelAdmin,
  BattlePassPayload,
  BattlePassSeasonAdmin,
} from "./types";

export type BattlePassTrack = "free" | "premium";

async function readJson<T>(r: Response): Promise<T> {
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) {
    const err = new Error(data.error || `battle-pass ${r.status}`);
    (err as Error & { code?: string }).code = data.error;
    throw err;
  }
  return data;
}

export async function fetchBattlePass(): Promise<BattlePassPayload> {
  const r = await fetch(apiPath("/api/battle-pass"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) return { active: null, claimableCount: 0 };
  return readJson<BattlePassPayload>(r);
}

export async function reportBattlePassRun(body: {
  modeId: string;
  outcome?: "play" | "win";
  score?: number;
  isNewRecord?: boolean;
}): Promise<BattlePassPayload & { battlePass?: { granted: number; levelsGained: number; level: number; xp: number; xpToNext: number } }> {
  const r = await fetch(apiPath("/api/battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "report-run", ...body }),
  });
  return readJson(r);
}

export async function claimBattlePassReward(level: number, track: BattlePassTrack) {
  const r = await fetch(apiPath("/api/battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "claim", level, track }),
  });
  return readJson<BattlePassPayload & { reward?: { label: string; kind: string; dots: number; imageData: string | null } }>(r);
}

export async function claimAllBattlePassRewards() {
  const r = await fetch(apiPath("/api/battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "claim-all" }),
  });
  return readJson<BattlePassPayload & { claimedCount: number }>(r);
}

export async function buyBattlePassPremium() {
  const r = await fetch(apiPath("/api/battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "buy-premium" }),
  });
  return readJson<BattlePassPayload & { pricePaid?: number }>(r);
}

export async function fetchAdminBattlePassSeasons(): Promise<BattlePassSeasonAdmin[]> {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await readJson<{ seasons: BattlePassSeasonAdmin[] }>(r);
  return data.seasons ?? [];
}

export async function fetchAdminBattlePassSeason(id: string): Promise<{
  season: BattlePassSeasonAdmin;
  levels: BattlePassLevelAdmin[];
  warnings: string[];
}> {
  const r = await fetch(`${apiPath("/api/admin-battle-pass")}?id=${encodeURIComponent(id)}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return readJson(r);
}

export async function createAdminBattlePassSeason(body: Partial<BattlePassSeasonAdmin>) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "season", ...body }),
  });
  return readJson<{ season: BattlePassSeasonAdmin; levels: BattlePassLevelAdmin[] }>(r);
}

export async function patchAdminBattlePassSeason(id: string, body: Record<string, unknown>) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id, ...body }),
  });
  return readJson<{ season: BattlePassSeasonAdmin }>(r);
}

export async function patchAdminBattlePassLevel(
  seasonId: string,
  level: number,
  body: Record<string, unknown>,
) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id: seasonId, section: "level", level, ...body }),
  });
  return readJson<{ level: BattlePassLevelAdmin }>(r);
}

export async function publishAdminBattlePassSeason(id: string, force = false) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "publish", id, force }),
  });
  return readJson<{ season: BattlePassSeasonAdmin; warnings?: string[] }>(r);
}

export async function endAdminBattlePassSeason(id: string) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "end", id }),
  });
  return readJson<{ season: BattlePassSeasonAdmin }>(r);
}

export async function deleteAdminBattlePassSeason(id: string) {
  const r = await fetch(`${apiPath("/api/admin-battle-pass")}?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return readJson<{ ok: boolean }>(r);
}

export async function fetchAdminBattlePassPickers(section: "cosmetics" | "shop-items" | "crates") {
  const r = await fetch(`${apiPath("/api/admin-battle-pass")}?section=${section}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return readJson<{
    cosmetics?: Array<{ id: string; name: string; category: string; imageData: string }>;
    crates?: Array<{ id: string; name: string; imageData: string }>;
  }>(r);
}

export async function createAdminBattlePassCosmetic(body: Record<string, unknown>) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "cosmetic", ...body }),
  });
  return readJson<{ cosmetic: { id: string; name: string; category: string; imageData: string } }>(r);
}

export async function patchAdminBattlePassCosmetic(body: Record<string, unknown>) {
  const r = await fetch(apiPath("/api/admin-battle-pass"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "cosmetic", ...body }),
  });
  return readJson<{ cosmetic: { id: string; name: string; category: string; imageData: string } }>(r);
}

export async function fetchAdminBattlePassCosmetic(id: string) {
  const r = await fetch(
    `${apiPath("/api/admin-battle-pass")}?section=cosmetic&id=${encodeURIComponent(id)}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  return readJson<{ cosmetic: Record<string, unknown> & { id: string; name: string; imageData: string } }>(
    r,
  );
}
